import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { isNil } from 'ramda';
import SendVoucherEmailCTA from 'components/shared/SendVoucherEmailCTA';
import UpdateVoucherCTA from 'components/shared/UpdateVoucherCTA';
import { Column, Row } from 'components/shared/Layout';
import VoucherConfirmModal from 'components/shared/VoucherConfirmModal';
import { erVoucherTypes, esVoucherTypes, VoucherStatus } from 'constants/vouchers';
import { useManagedMutation } from 'utils/managedHooks';
import VoucherDetailsTable from './VoucherDetailsTable';
import CustomerDetailsTable from './CustomerDetailsTable';
import { StyledH2 } from './VoucherDetails.styles';
import UPDATE_VOUCHER from './mutations/UPDATE_VOUCHER';
import { VoucherEditSettings } from './useVoucherEditSettings';

const DATE_FORMAT = 'YYYY-MM-DD';

const VoucherDetails = ({
  voucherAlias,
  voucherOriginalValue,
  voucherCurrentBalance,
  voucherCurrency,
  voucherType,
  voucherDescription,
  voucherStatus,
  associatedPNR,
  salesPeriodStart,
  salesPeriodEnd,
  travelPeriodStart,
  travelPeriodEnd,
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
  activatedAt,
  inactivatedAt,
  language,
  firstName,
  lastName,
  email,
  canDisableVoucher,
  canEnableVoucher,
  canUpdateVoucher,
  associatedVoucher,
  replacedByVoucher,
  originalSalesPeriodEnd,
  originalTravelPeriodEnd
}) => {
  const history = useHistory();
  const [updateVoucher, { loading }] = useManagedMutation(UPDATE_VOUCHER);
  const { register, handleSubmit, formState, reset, getValues } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: email || '',
      voucherStatus: VoucherStatus[voucherStatus]
    }
  });

  const { errors } = formState;

  const hasVoucherExpired = voucherStatus === 'EXPIRED';
  const [isEditMode, setEditMode] = useState(false);
  const [isConfirmationOpened, setConfirmationOpened] = useState(false);
  const isConfirmationEnabled = formState.isValid && formState.isDirty;
  const isEsOrErVoucher = esVoucherTypes.includes(voucherType) || erVoucherTypes.includes(voucherType);

  const onEditSubmit = () => {
    setConfirmationOpened(true);
  };

  const onConfirmSubmit = async ({ reason }) => {
    const values = getValues();
    const variables = {
      voucherAlias,
      reason
    };
    if (!isNil(values.email) && values.email !== email) {
      variables.recipientEmail = values.email;
    }
    if (!isNil(values.voucherStatus) && values.voucherStatus !== VoucherStatus[voucherStatus]) {
      variables.active = values.voucherStatus === VoucherStatus.ACTIVE;
    }

    const originalSalesPeriodEnd = moment(salesPeriodEnd, DATE_FORMAT).startOf('day');
    const originalTravelPeriodEnd = moment(travelPeriodEnd, DATE_FORMAT).startOf('day');
    const newSalesPeriodEnd = moment(values.salesPeriodEnd, DATE_FORMAT).startOf('day');

    if (!originalSalesPeriodEnd.isSame(newSalesPeriodEnd)) {
      variables.salesPeriodEnd = newSalesPeriodEnd.format(DATE_FORMAT);
      if (isEsOrErVoucher) {
        variables.externalReferencesDates = {
          originalSalesPeriodEnd: originalSalesPeriodEnd.format(DATE_FORMAT),
          originalTravelPeriodEnd: originalTravelPeriodEnd.format(DATE_FORMAT)
        };
      }
    }

    await updateVoucher({ variables });
    history.go();
  };

  const onCancelEdit = () => {
    reset();
    setEditMode(false);
  };

  return (
    <VoucherEditSettings
      voucherType={voucherType}
      voucherStatus={voucherStatus}
      isEditMode={isEditMode}
      canUpdateVoucher={canUpdateVoucher}
      canDisableVoucher={canDisableVoucher}
      canEnableVoucher={canEnableVoucher}
    >
      <VoucherConfirmModal
        isOpen={isConfirmationOpened}
        isLoading={loading}
        onToggle={() => setConfirmationOpened(!isConfirmationOpened)}
        onSubmit={onConfirmSubmit}
        showUpdateWarnings
        voucherType={voucherType}
      />
      <form data-cy="voucher-form" onSubmit={handleSubmit(onEditSubmit)}>
        <StyledH2 paddingTop="30px">
          <FormattedMessage id="vouchers.voucherDetails" />
        </StyledH2>
        <VoucherDetailsTable
          voucherAlias={voucherAlias}
          voucherCurrency={voucherCurrency}
          voucherOriginalValue={voucherOriginalValue}
          voucherCurrentBalance={voucherCurrentBalance}
          voucherDescription={voucherDescription}
          voucherStatus={voucherStatus}
          voucherType={voucherType}
          salesPeriodStart={salesPeriodStart}
          salesPeriodEnd={salesPeriodEnd}
          travelPeriodStart={travelPeriodStart}
          travelPeriodEnd={travelPeriodEnd}
          createdBy={createdBy}
          createdAt={createdAt}
          updatedBy={updatedBy}
          updatedAt={updatedAt}
          activatedAt={activatedAt}
          inactivatedAt={inactivatedAt}
          language={language}
          associatedPNR={associatedPNR}
          associatedVoucher={associatedVoucher}
          replacedByVoucher={replacedByVoucher}
          register={register}
          originalSalesPeriodEnd={originalSalesPeriodEnd}
          originalTravelPeriodEnd={originalTravelPeriodEnd}
        />
        <StyledH2>
          <FormattedMessage id="vouchers.customerDetails" />
        </StyledH2>
        <CustomerDetailsTable firstName={firstName} lastName={lastName} register={register} errors={errors} />
        <Row>
          <Column>
            <SendVoucherEmailCTA voucherAlias={voucherAlias} />
            <UpdateVoucherCTA
              onEditClick={() => setEditMode(true)}
              onCancelClick={onCancelEdit}
              isEditMode={isEditMode}
              isConfirmEnabled={isConfirmationEnabled}
              hasVoucherExpired={hasVoucherExpired}
            />
          </Column>
        </Row>
      </form>
    </VoucherEditSettings>
  );
};

VoucherDetails.propTypes = {
  voucherAlias: PropTypes.string,
  voucherOriginalValue: PropTypes.number,
  voucherCurrentBalance: PropTypes.number,
  voucherCurrency: PropTypes.string,
  voucherType: PropTypes.string,
  voucherStatus: PropTypes.string,
  voucherDescription: PropTypes.string,
  associatedPNR: PropTypes.string,
  salesPeriodStart: PropTypes.string,
  salesPeriodEnd: PropTypes.string,
  travelPeriodStart: PropTypes.string,
  travelPeriodEnd: PropTypes.string,
  createdBy: PropTypes.string,
  createdAt: PropTypes.string,
  updatedBy: PropTypes.string,
  updatedAt: PropTypes.string,
  activatedAt: PropTypes.string,
  inactivatedAt: PropTypes.string,
  language: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  canDisableVoucher: PropTypes.bool,
  canEnableVoucher: PropTypes.bool,
  canUpdateVoucher: PropTypes.bool,
  associatedVoucher: PropTypes.string,
  replacedByVoucher: PropTypes.string,
  originalSalesPeriodEnd: PropTypes.string,
  originalTravelPeriodEnd: PropTypes.string
};

export default VoucherDetails;
